import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { RELEASE_NOTES, type ReleaseNote } from '../data/releaseNotes';

/**
 * Application-update notifications for the sidebar bell.
 *
 * Two sources:
 *  - Release notes bundled with the build (data/releaseNotes.ts). Read state
 *    is kept per user in this browser's localStorage.
 *  - New-deploy detection: in a production build the app re-fetches
 *    index.html every few minutes (and when the tab regains focus) and
 *    compares its hashed main bundle with the one running. A different hash
 *    means a newer version is live, so the user is asked to refresh.
 *
 * Lives above the routes because DashboardLayout can remount between pages.
 */
interface NotificationsContextValue {
  notes: ReleaseNote[];
  isUnread: (id: string) => boolean;
  /** Unread release notes, plus one while a new version is waiting. */
  unreadCount: number;
  markAllRead: () => void;
  updateAvailable: boolean;
  /** The refresh banner was closed; the bell still shows the update. */
  bannerDismissed: boolean;
  dismissBanner: () => void;
}

const NotificationsContext = createContext<NotificationsContextValue>({
  notes: [],
  isUnread: () => false,
  unreadCount: 0,
  markAllRead: () => {},
  updateAvailable: false,
  bannerDismissed: false,
  dismissBanner: () => {},
});

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const MAIN_BUNDLE_RE = /static\/js\/main\.[0-9a-f]+\.js/;

const storageKey = (userId: string) => `notificationsRead:${userId}`;

const loadReadIds = (userId: string): Set<string> => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

/** The hashed main bundle this page is running, or null in development
 *  (the dev server's bundle.js has no hash, so there is nothing to compare). */
const runningBundle = (): string | null => {
  const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'));
  for (const s of scripts) {
    const m = s.getAttribute('src')?.match(MAIN_BUNDLE_RE);
    if (m) return m[0];
  }
  return null;
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const userId = user?.UserID ?? 'anonymous';
  const [readIds, setReadIds] = useState<Set<string>>(() => loadReadIds(userId));
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    setReadIds(loadReadIds(userId));
  }, [userId]);

  const markAllRead = useCallback(() => {
    const ids = RELEASE_NOTES.map((n) => n.id);
    setReadIds(new Set(ids));
    try {
      localStorage.setItem(storageKey(userId), JSON.stringify(ids));
    } catch {
      // Storage full or blocked: read state just won't survive a reload.
    }
  }, [userId]);

  useEffect(() => {
    const current = runningBundle();
    if (!current) return undefined;
    let stopped = false;

    const check = async () => {
      if (stopped || document.visibilityState === 'hidden') return;
      try {
        const res = await fetch(`${process.env.PUBLIC_URL}/index.html`, { cache: 'no-store' });
        if (!res.ok) return;
        const latest = (await res.text()).match(MAIN_BUNDLE_RE)?.[0];
        if (latest && latest !== current && !stopped) {
          setUpdateAvailable(true);
          stopped = true;
        }
      } catch {
        // Offline or a blip: try again next time.
      }
    };

    const timer = window.setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const value = useMemo<NotificationsContextValue>(() => {
    const isUnread = (id: string) => !readIds.has(id);
    const unreadNotes = RELEASE_NOTES.filter((n) => isUnread(n.id)).length;
    return {
      notes: RELEASE_NOTES,
      isUnread,
      unreadCount: unreadNotes + (updateAvailable ? 1 : 0),
      markAllRead,
      updateAvailable,
      bannerDismissed,
      dismissBanner: () => setBannerDismissed(true),
    };
  }, [readIds, updateAvailable, bannerDismissed, markAllRead]);

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export const useNotifications = (): NotificationsContextValue => useContext(NotificationsContext);
